// Graupner Butler B-3 – Dialog zur Taste „Fehler gefunden“ / „Nicht reproduzierbar“.
// Legt eine neue Fehlerakte (BUT-0001 …) an oder hängt eine Wiederholung an eine offene Akte.
// Markiert werden nur Vorgangskennungen aus dieser Butler-Sitzung; Freitext kommt nur aus den Eingabefeldern.
// BUT-0002 (15.10.2026): Nach „Fehlerakte anlegen“ bleibt der Dialog mit „An Claude senden“ (Teilen-Menü,
// nur wenn das Gerät es kann) bzw. „Bericht kopieren“ offen. Der Bericht ist Admin-Sache (wie in der Fehlerakte).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { butlerVorgaenge, programmVersion } from "@/lib/ablauf";
import { teilenMoeglich, berichtVorabLaden, anClaudeSenden, berichtKopieren } from "@/lib/butlerTeilen";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ERGEBNIS_TEXT = {
  gespeichert: "gespeichert", ohne_aenderung: "ohne Änderung", abgebrochen: "abgebrochen",
  seite_verlassen: "Seite verlassen", fehler: "Fehler", ok: "ok",
};

const istAdmin = () => {
  try { return (JSON.parse(localStorage.getItem("user") || "null") || { role: "admin" }).role === "admin"; } catch { return false; }
};

const uhrzeit = (ms) => new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// art: "fehler" (Taste „Fehler gefunden“) oder "ohne_fehler" (Taste „Nicht reproduzierbar“)
export default function FehlerGefundenDialog({ offen, art = "fehler", onClose }) {
  const [vorgaenge, setVorgaenge] = useState([]);
  const [gewaehlt, setGewaehlt] = useState(new Set());
  const [akten, setAkten] = useState([]);
  const [ziel, setZiel] = useState("neu"); // "neu" oder Aktenzeichen
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [prio, setPrio] = useState("normal");
  const [sendet, setSendet] = useState(false);
  const [erfolg, setErfolg] = useState(null); // { aktenzeichen, vorgaenge } nach „Fehlerakte anlegen“
  const [berichtText, setBerichtText] = useState(null); // vorab geladen für „An Claude senden“

  useEffect(() => {
    if (!offen) return;
    const liste = butlerVorgaenge().reverse(); // neueste zuerst
    setVorgaenge(liste);
    // Vorschlag: der zuletzt begonnene Vorgang (Hauptvorgang, nicht Untervorgang)
    const vorschlag = liste.find((v) => !v.eltern) || liste[0];
    setGewaehlt(new Set(vorschlag ? [vorschlag.id] : []));
    setTitel(""); setBeschreibung(""); setPrio("normal");
    setZiel(art === "fehler" ? "neu" : "");
    setErfolg(null); setBerichtText(null);
    api.get("/butler/fehler-auswahl").then((r) => {
      setAkten(r.data || []);
      if (art !== "fehler" && r.data?.length) setZiel(r.data[0].aktenzeichen);
    }).catch(() => setAkten([]));
  }, [offen, art]);

  const umschalten = (id) => setGewaehlt((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const absenden = async () => {
    const vorgang_ids = vorgaenge.filter((v) => gewaehlt.has(v.id)).map((v) => v.id);
    setSendet(true);
    try {
      if (ziel === "neu") {
        if (!titel.trim()) { toast.error("Bitte kurz beschreiben, was falsch war."); return; }
        const r = await api.post("/butler/fehler", {
          titel: titel.trim(), beschreibung: beschreibung.trim(), prio, vorgang_ids,
          seite: window.location.pathname, version_client: programmVersion(),
        });
        toast.success(`Fehlerakte ${r.data.aktenzeichen} angelegt`, { description: `${r.data.vorgaenge} Vorgang/Vorgänge markiert` });
        setErfolg({ aktenzeichen: r.data.aktenzeichen, vorgaenge: r.data.vorgaenge });
        if (istAdmin() && teilenMoeglich()) berichtVorabLaden(r.data.aktenzeichen).then(setBerichtText).catch(() => {});
        return; // Dialog bleibt offen: An Claude senden / Bericht kopieren / Schließen
      } else if (ziel) {
        const r = await api.post(`/butler/fehler/${ziel}/markieren`, { ergebnis: art, vorgang_ids, bemerkung: beschreibung.trim() });
        toast.success(`${ziel}: Wiederholung vermerkt`, { description: `Status jetzt: ${r.data.status.replace(/_/g, " ")}` });
      } else {
        toast.error("Bitte eine Fehlerakte wählen."); return;
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte nicht gespeichert werden");
    } finally { setSendet(false); }
  };

  const senden = async () => {
    try {
      const ergebnis = await anClaudeSenden(erfolg.aktenzeichen, berichtText);
      if (ergebnis === "geteilt") { toast.success(`${erfolg.aktenzeichen} an das Teilen-Menü übergeben`); onClose(); }
    } catch {
      toast.error("Teilen nicht möglich – bitte „Bericht kopieren“ verwenden");
    }
  };
  const kopieren = async () => {
    try {
      const { kopiert } = await berichtKopieren(erfolg.aktenzeichen);
      if (kopiert) toast.success("Fehlerbericht kopiert", { description: "Jetzt in den Chat einfügen." });
      else toast.info("Kopieren nicht möglich – Bericht in der Fehlerakte öffnen.");
    } catch { toast.error("Fehlerbericht nicht erstellt"); }
  };

  if (erfolg) {
    const admin = istAdmin(), teilen = teilenMoeglich();
    return (
      <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-lg z-[10000]" data-testid="fehler-erfolg">
          <DialogHeader>
            <DialogTitle>Fehlerakte {erfolg.aktenzeichen} angelegt</DialogTitle>
            <DialogDescription>
              {erfolg.vorgaenge} Vorgang/Vorgänge markiert.{admin ? " Den Fehlerbericht jetzt weitergeben?" : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            {admin && teilen && (
              <button type="button" disabled={!berichtText} onClick={senden} data-testid="btn-an-claude-senden"
                className="rounded bg-indigo-700 px-3 py-2 font-semibold text-white hover:bg-indigo-800 disabled:opacity-50">
                {berichtText ? "An Claude senden" : "Bericht wird vorbereitet …"}
              </button>
            )}
            {admin && !teilen && (
              <button type="button" onClick={kopieren} data-testid="btn-bericht-kopieren"
                className="rounded bg-indigo-700 px-3 py-2 font-semibold text-white hover:bg-indigo-800">
                Bericht kopieren
              </button>
            )}
            <button type="button" className="rounded border px-3 py-2" onClick={onClose}>Schließen</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg z-[10000]">
        <DialogHeader>
          <DialogTitle>{art === "fehler" ? "Fehler gefunden" : "Nicht reproduzierbar"}</DialogTitle>
          <DialogDescription>
            {art === "fehler"
              ? "Markiert die gewählten Vorgänge und legt eine Fehlerakte an – oder vermerkt eine Wiederholung."
              : "Der Ablauf wurde wiederholt, der Fehler trat diesmal nicht auf."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="font-medium">Fehlerakte</span>
            <select className="mt-1 w-full rounded border px-2 py-1" value={ziel} onChange={(e) => setZiel(e.target.value)}>
              {art === "fehler" && <option value="neu">Neue Fehlerakte anlegen</option>}
              {art !== "fehler" && !akten.length && <option value="">– keine offene Akte –</option>}
              {akten.map((a) => <option key={a.aktenzeichen} value={a.aktenzeichen}>{a.titel}</option>)}
            </select>
          </label>

          {ziel === "neu" && (
            <>
              <label className="block">
                <span className="font-medium">Was war falsch?</span>
                <input className="mt-1 w-full rounded border px-2 py-1" maxLength={200} value={titel}
                  onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Angebot speichert Position nicht" />
              </label>
              <label className="block">
                <span className="font-medium">Priorität</span>
                <select className="mt-1 w-full rounded border px-2 py-1" value={prio} onChange={(e) => setPrio(e.target.value)}>
                  <option value="hoch">Hoch</option><option value="normal">Normal</option><option value="niedrig">Niedrig</option>
                </select>
              </label>
            </>
          )}

          <label className="block">
            <span className="font-medium">{ziel === "neu" ? "Beschreibung (optional)" : "Bemerkung (optional)"}</span>
            <textarea className="mt-1 w-full rounded border px-2 py-1" rows={3} maxLength={2000} value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)} />
            <span className="text-xs text-slate-500">Bitte keine Passwörter oder Kundendaten eintragen.</span>
          </label>

          <div>
            <span className="font-medium">Vorgänge dieser Butler-Sitzung</span>
            {!vorgaenge.length && <p className="text-slate-500">Noch keine Vorgänge aufgezeichnet.</p>}
            <ul className="mt-1 max-h-48 overflow-auto rounded border divide-y">
              {vorgaenge.map((v) => (
                <li key={v.id} className={`flex items-center gap-2 px-2 py-1 ${v.eltern ? "pl-6" : ""}`}>
                  <input type="checkbox" checked={gewaehlt.has(v.id)} onChange={() => umschalten(v.id)} />
                  <span className="flex-1">{v.name}</span>
                  <span className="text-xs text-slate-500">{uhrzeit(v.zeit)} · {v.ergebnis ? ERGEBNIS_TEXT[v.ergebnis] || v.ergebnis : "läuft"}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <button type="button" className="rounded border px-3 py-1" onClick={onClose}>Abbrechen</button>
          <button type="button" disabled={sendet} onClick={absenden}
            className="rounded bg-red-600 px-3 py-1 font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            {ziel === "neu" ? "Fehlerakte anlegen" : "Vermerken"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
